import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * ⭐ A BADGE IS A TINT PLUS AN INK, AND THE TWO MOVE TOGETHER.
 *
 * Every role below pairs `bg-x-100` with `text-x-700` and `border-x-200`. That
 * pairing is the whole contract, and it is why a call site must pick a VARIANT
 * rather than hand-painting a fill: passing `className="bg-success"` overrides
 * the FILL and nothing else, so the ink stays whatever the previous variant
 * chose and you get dark role ink on a saturated green — unreadable, and
 * invisible in review because the tokens themselves are all fine.
 *
 * If a call site genuinely needs a solid fill (a safety shout, a notification
 * dot) it must pass fill AND ink together: `bg-destructive text-white`.
 *
 * The ladder inverts in dark mode but the names do not, so these strings are
 * correct in both without a single `dark:` utility — which is also why the old
 * raw-palette variants — a hardcoded green-100 fill with green-800 ink, and
 * its three siblings — had to go: they painted
 * a pale daylight chip onto a dark screen, and they ignored the tenant's brand
 * entirely.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        /* The quiet brand chip: a classification, not a state. */
        brand: "border-primary-100 bg-primary-100 text-primary-700",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        /* Solid destructive is the one sanctioned safety shout — a panic
           value or a rejected specimen should not read as a quiet chip. */
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        /* ── the clinical roles, as tint + ink ─────────────────────────── */
        success: "border-success-200 bg-success-100 text-success-700",
        warning: "border-warning-200 bg-warning-100 text-warning-700",
        info: "border-info-200 bg-info-100 text-info-700",
        /* `error` is the quiet form of destructive — a failed row in a list,
           where a solid red pill in every row would be a wall of alarm. */
        error: "border-destructive-200 bg-destructive-100 text-destructive-700",
        processing: "border-info-200 bg-info-100 text-info-700 animate-pulse",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
